<script lang="ts">
    import { LocalDate } from '@/time'
    import { _ } from 'svelte-i18n';
    import { getContext, onMount, setContext} from 'svelte';
    import route from '../Util/routes';
    import {waitForForecastWorkouts, fetchTrainingAdvisor, StrToProgramType, createChallengePlan, createContinuousPlan, createEventPlan, createGoalPlan, createBbpPlan, createRacePlan, switchMyFitnessTab, type XSSDetails, baseRequest} from '../Util/utils';
    import { 
        ProgramType, 
        programFocusTypes, 
        programTypeIcon,
        xfaiProgramTypes,
        shortProgramTypeTitle,
    } from '../Common/constants';
    import type { 
        ProgramFocusTypes, 
        GoalPlan,
        EventPlan, 
        RacePlan, 
        ContinuousPlan,
        BbpPlan,
        ChallengePlan,
        AllXfaiPlans,
        AllTrainingPlans,
        AllXataPlans,
        RaceRecords,
        TabNames,
    } from '../Common/constants';
    import type { MyFitnessStores } from '../MyFitness/stores';
    import GoalSetupForm from './GoalSetupForm.svelte'
    import EventSetupForm from './EventSetupForm.svelte'
    import RaceSetupForm from './RaceSetupForm.svelte';
    import ContinuousSetupForm from './ContinuousSetupForm.svelte';
    import BBPSetupForm from './BBPeakSetupForm.svelte';
    import ChallengeSetupForm from './ChallengeSetupForm.svelte';
    import XfaiProgramPage from './XfaiProgramPage.svelte';
    import { writable } from 'svelte/store';
    import XataProgramPage from './XataProgramPage.svelte';
    import {Modal} from '@/Bootstrap';
    import CurrentProgram from './CurrentProgram.svelte';
    import LibLoader from '../Util/LibLoader.svelte';
    import {XfaiBall ,XataLogo ,InfoIcon, CyclingProgress} from '../Common'
    import { addDays } from 'date-fns';

    export let gmapsKey: string

    let root: HTMLElement

    let isChangingPlan = false
    export function openChangeProgram() { // For export in blade file
        isChangingPlan = true
        switchMyFitnessTab("program", root)
    }

    /* =========================================
        Creating Global State from Blade File 
    ============================================ */
    const { trainingAdvice, trainingAdviceUpdatedAt, trainingPlan, userParams, cal_settings } = getContext<MyFitnessStores>('myFitness')

    // Limiting the athletes available on this page.
    $userParams.athletes = Object.keys($userParams.athletes)
        .filter((a: ProgramFocusTypes) => programFocusTypes.includes(a))
        .reduce((obj, key) => {
            obj[key] = $userParams.athletes[key];
            return obj;
        }, {})

    const goalPlan = writable<GoalPlan>()
    const eventPlan = writable<EventPlan>()
    const racePlan = writable<RacePlan>()
    const continuousPlan = writable<ContinuousPlan>()
    const bbpPlan = writable<BbpPlan>()
    const challengePlan = writable<ChallengePlan>()

    let currentTP_raceRecords: RaceRecords = null
    let logoURL: string
    function setPlanDefaults() {
        $goalPlan = createGoalPlan($trainingPlan, $trainingAdvice, $userParams, $cal_settings)
        $eventPlan = createEventPlan($trainingPlan, $trainingAdvice, $userParams, $cal_settings)
        $racePlan = createRacePlan($trainingPlan, $trainingAdvice, $userParams, $cal_settings)
        $continuousPlan = createContinuousPlan($trainingPlan, $trainingAdvice, $userParams, $cal_settings)
        $bbpPlan = createBbpPlan($trainingPlan, $trainingAdvice, $userParams, $cal_settings)
        $challengePlan = createChallengePlan($trainingPlan, $trainingAdvice, $userParams, $cal_settings)
        
        if (currentTP_raceRecords) {
            // On the first call, the branch won't run, and the reactive statement will append records when they are ready
            // On subsequent calls, branch needs to run since the reactive statement won't trigger again
            onRaceRecordsUpdated(currentTP_raceRecords.race_data, currentTP_raceRecords.original_time, currentTP_raceRecords.original_power, currentTP_raceRecords.polyline, logoURL)
        }
    }
    

    const onRaceRecordsUpdated = (raceData, originalTime, originalPower, polyline, logoURL) => {
        $racePlan.raceRecords = raceData
        $racePlan.raceRecords.originalTime = originalTime
        $racePlan.raceRecords.originalPower = originalPower
        $racePlan.raceRecords.polyline = polyline
        $racePlan.raceLogoUrl = logoURL
    }
    $: if (!!currentTP_raceRecords?.race_data) onRaceRecordsUpdated(currentTP_raceRecords.race_data, currentTP_raceRecords.original_time, currentTP_raceRecords.original_power, currentTP_raceRecords.polyline, logoURL)

    // console.log($goalPlan, $eventPlan, $racePlan, $continuousPlan, $bbpPlan, $challengePlan)

    let currentProgramType = StrToProgramType($trainingPlan?.program_type, $trainingPlan?.selected_mode ?? "none")
    let selectedProgramType;
    let switchToProgramType = getContext('switchToProgramType');
    if (switchToProgramType == 'race') {
      selectedProgramType = ProgramType.Race
      openChangeProgram()
    } else {
      selectedProgramType = currentProgramType
    }
    $: if ($trainingPlan && $cal_settings && $trainingAdvice) { 
        setPlanDefaults()
        currentProgramType = StrToProgramType($trainingPlan?.program_type, $trainingPlan?.selected_mode ?? "none")
    }
    

    /*===================================================================================================*/
    
    //@ts-ignore
    let Xert = window.Xert

    /* =========================================
        Update the training plan 
    ============================================ */
    function setTrainingAdvice(training_advice) {
        if(training_advice){
            if (training_advice.timestamp)
                trainingAdviceUpdatedAt.set(new Date(training_advice.timestamp))
            else 
                trainingAdviceUpdatedAt.set(new Date())
            trainingAdvice.set(training_advice);
        }
    }
    
    function setTrainingPlan(t_plan) {
        if(t_plan){
            trainingPlan.set(t_plan);
        }
    }
    
    function resetForecastResults() {
        forecastResults = {
            [ProgramType.Goal]:     null,
            [ProgramType.Event]:    null,
            [ProgramType.Race]:     null,
        }
    }
    let forecastResults = {
        [ProgramType.Goal]:     null,
        [ProgramType.Event]:    null,
        [ProgramType.Race]:     null,
    }
    let cancelForecast = () => {};

    function ProgramTypeConversion(type:ProgramType){
        switch (type) {
            case ProgramType.BaseBuildPeak:   return {program_type: "targetEvent",    selected_mode:"none"}
            case ProgramType.Continuous:            return {program_type: "continuous",     selected_mode:"none"}
            case ProgramType.Challenge:             return {program_type: "challenge",      selected_mode:"none"} 
            case ProgramType.Event:                 return {program_type: "targetEvent",    selected_mode:"target_xss"}
            case ProgramType.Goal:                  return {program_type: "targetEvent",    selected_mode:"target_focus"}
            case ProgramType.Race:                  return {program_type: "targetEvent",    selected_mode:"target_race"}
            default:                                return {program_type: "targetEvent",    selected_mode:"none"}
        }
    }

    function basePlanPayload(plan: AllTrainingPlans) {
        return {
            training_advice_as_of: $trainingAdvice.training_advice_as_of_val,
            focus: plan.targetAthlete,
            settings: plan.settings,
            cal_settings: plan.cal_settings,
            ...ProgramTypeConversion(selectedProgramType),
        }
    }

    function baseXataPayload(plan: AllXataPlans) {
        return {
            ...basePlanPayload(plan),
            improvement_rate: plan.irValue,
        }
    }

    function baseXfaiPayload(plan: AllXfaiPlans) {
        return {
            ...basePlanPayload(plan),
            target_event_date: plan.targetDate.toISOString(),
            specificity: plan.specificity,
            // Future start date: plan is scheduled from then, Pre-Plan Maintenance Program until it
            ...(plan.startDate ? { planned_start_date: LocalDate.format(plan.startDate), fromDate: plan.startDate.toISOString() } : {}), // planned_start_date is date only, the server reads it in the profile timezone
            result: forecastResults[selectedProgramType],
            schedule_workouts: scheduleWorkouts,
        }
    }

    function goalPlanBody(plan: GoalPlan) {
        const body = new FormData()
        let payload = {
            ...baseXfaiPayload(plan),
            focus_power: plan.focusPower,
            goal_info: {
                targets_mode: plan.targetsMode,
                manual_signature: plan.targetsMode === "manual" ? plan.manualSignature : null,
            },
        }
        body.append('payload', JSON.stringify(payload))
        return body
    }

    function eventPlanBody(plan: EventPlan) {
        const body = new FormData()
        let payload = {
            ...baseXfaiPayload(plan),
            target_xss: plan.xss,
            name: plan.name,
            event_info: {
                type: plan.type,
                terrain: plan.terrain,
                duration: plan.duration,
                targets_mode: plan.targetsMode,
                manual_xss: plan.targetsMode === "manual" ? plan.manualXss : null,
            }
        }
        body.append('payload', JSON.stringify(payload))
        return body
    }

    function racePlanBody(plan: RacePlan) {
        const body = plan.fileData ?? new FormData()
        let payload = {
            ...baseXfaiPayload(plan),
            target_xss: plan.xss,
            name: plan.name,
            path: plan.activityPath,
            race_info: {
                user_target_weight: plan.userTargetWeight,
                rider_weight: plan.riderWeight,
                speed_scale: plan.raceSpeedScale,
                power_scale: plan.racePowerScale,
                difficulty_score: plan.difficultyScore,
                carbs_per_hour: plan.carbsPerHour,
                ignore_carbs: plan.ignoreCarbs,
                ignore_difficulty: plan.ignoreDifficulty,
            }
        }
        body.append('payload', JSON.stringify(payload))
        return body
    }

    function continuousPlanBody(plan: ContinuousPlan) {
        const body = new FormData()
        let payload = {
            ...baseXataPayload(plan),
        }
        body.append('payload', JSON.stringify(payload))
        return body
    }

    function bbpPlanBody(plan: BbpPlan) {
        const body = new FormData()
        let payload = {
            ...baseXataPayload(plan),
            specificity: plan.specificity,
            target_event_date: plan.targetDate.toISOString(),
        }
        body.append('payload', JSON.stringify(payload))
        return body
    }

    function challengePlanBody(plan: ChallengePlan) {
        const body = new FormData()
        let payload = {
            ...baseXataPayload(plan),
            target_event_date: plan.targetDate.toISOString(),
        }
        body.append('payload', JSON.stringify(payload))
        return body
    }

    
    // NOTE: udpateInProgress is local variable to track update request, savingResults is passed to other components and has additional logic in it.
    let savingResults = false
    // Assign workouts to the forecasted days when saving a Forecast AI plan (see XfaiProgramPage)
    let scheduleWorkouts = true
    let updateInProgress = false

    function updateTrainingProgram() {
        // Don't save plan while previous hasn't finished saving, this can create duplicate plans on the server.
        if (updateInProgress) return

        savingResults = true
        updateInProgress = true
        isChangingPlan = false
        window.scrollTo(0,0) // scroll to top
        const body = getFormData()
        cal_settings.set(JSON.parse(body.get("payload") as string).cal_settings)

        // NOTE: Trick to prevent event handler in MyFitness.svelte from reloading training advice
        // setTrainingAdvice() will correct the timestamp later
        trainingAdviceUpdatedAt.set(addDays(new Date(), 1))

        // Workouts a saved plan couldn't get straight away are generated in the background
        const workoutsComplete = waitForForecastWorkouts()
        body.append('save_id', workoutsComplete.saveId)

        // Make and handle the request
        return window.fetch(route('UserController@postTrainingProgram'), {
            ...baseRequest(),
            method: "POST",
            body: body
        }).then(response=>response.json()).then(async data=>{
            if (data.workouts_pending) {
                await workoutsComplete.promise
                // The response is from before the workouts were added: read the plan and advice again
                data = { ...data, ...(await fetchTrainingAdvisor().catch(() => ({}))) }
            } else workoutsComplete.cancel()
            resetForecastResults()
            if(data.result == false){
                Xert.warning("Failed to update program.");
            }
            else{
                setTrainingPlan(data.training_plan);
                setTrainingAdvice(data.training_advice);
            }
        }).catch(() => {
            // A network error or a response that isn't JSON: the save may not have happened.
            workoutsComplete.cancel()
            trainingAdviceUpdatedAt.set(new Date()) // Undo the trick above, which setTrainingAdvice() would have corrected
            Xert.warning("Failed to update program. Please try again.");
        }).finally(() => {
            // Otherwise the full-screen progress overlay stays up and no save can be started again.
            savingResults = false
            updateInProgress = false
        })
    }
    /*===================================================================================================*/

    const AIProgramTypes = [ProgramType.Goal, ProgramType.Event, ProgramType.Race]
    let programOptionBtns = [
        {name:shortProgramTypeTitle[ProgramType.Goal], id:"Goal", value:ProgramType.Goal,icon: programTypeIcon[ProgramType.Goal]},
        {name:shortProgramTypeTitle[ProgramType.Event], id:"Event", value:ProgramType.Event,icon:programTypeIcon[ProgramType.Event]},
        {name:shortProgramTypeTitle[ProgramType.Race], id:"Race", value:ProgramType.Race,icon:programTypeIcon[ProgramType.Race]},
        {name:shortProgramTypeTitle[ProgramType.BaseBuildPeak], id:"120-day-program", value:ProgramType.BaseBuildPeak, icon:programTypeIcon[ProgramType.BaseBuildPeak]},
        {name:shortProgramTypeTitle[ProgramType.Continuous], id:"Continuous", value:ProgramType.Continuous, icon:programTypeIcon[ProgramType.Continuous]},
        {name:shortProgramTypeTitle[ProgramType.Challenge], id:"Challenge", value:ProgramType.Challenge, icon:programTypeIcon[ProgramType.Challenge]},  
    ];

    // Modal controls and types
    let showModal: () => void
    let closeModal: () => void
    enum ModalType {
        Save,
        SwitchTab,
        CancelEdit,
    }
    let modalType = ModalType.Save

    // Save modal
    let confirmationWarning: string
    let getFormData: () => ReturnType<typeof goalPlanBody> | ReturnType<typeof eventPlanBody> | ReturnType<typeof racePlanBody> | ReturnType<typeof continuousPlanBody> | ReturnType<typeof bbpPlanBody> | ReturnType<typeof challengePlanBody>
    function openSaveModal(formDataGetter: typeof getFormData) {
        modalType = ModalType.Save
        getFormData = formDataGetter
        
        // const saveXfai = xfaiProgramTypes.includes(selectedProgramType)
        const currentXfai = xfaiProgramTypes.includes(currentProgramType)
        const selectedXfai = xfaiProgramTypes.includes(selectedProgramType)

        if ((adapt && selectedXfai) || !$trainingPlan?.hasForecast) {
            // If xfai plan adapted, or the old plan is not a forecast, don't show warning and just save immediately.
            return updateTrainingProgram()
        } else {
            confirmationWarning = currentXfai 
            ? "This will overwrite your existing program including all forecasted training." 
            : "Your current program will be lost."
            showModal()
        }
    }

    function changeProgramSetupForm() {
        cancelForecast() 
        resetForecastResults()
    }

    let isGoogleMapsLoaded = false
    
    let adapt = false

    $: showPlanForms = isChangingPlan || !$trainingPlan

    export function beforeMyFitnessTabChange(next) {
        if(isChangingPlan) {
            modalType = ModalType.SwitchTab
            showModal()
            nextTab = next
            return true
        }    
        return false
    }

    let nextTab: TabNames
</script>
    
<LibLoader url={`https://maps.googleapis.com/maps/api/js?key=${gmapsKey}&libraries=marker&region=CA`} on:loaded={() => isGoogleMapsLoaded = true} /> 

<svelte:window on:beforeunload={(e) => {if (isChangingPlan) e.preventDefault()}} />

<div bind:this={root} class="flex flex-col items-center justify-stretch gap-7">

    <CurrentProgram hidden={showPlanForms} editPlanCallback={() => { isChangingPlan = true; setPlanDefaults(); }} bind:raceRecords={currentTP_raceRecords} bind:logoURL {isGoogleMapsLoaded} />

    {#if showPlanForms}
        <div class="flex flex-col gap-4 border rounded border-black/10 px-8 py-8 text-base shadow-md shadow-black/10 max-w-[1800px]">

            <div class="flex flex-row items-center">
                <span class="text-lg text-slate-600">
                    Choose a Program <InfoIcon href="https://baronbiosys.com/glossary/program-type/" />
                </span>
                {#if $trainingPlan}
                    <button class="btn-xfai-sm ml-auto border border-slate-600 text-slate-600 bg-white hover:bg-[--color-xert-blue] hover:text-white hover:border-[--color-xert-blue] gap-2" 
                        on:click={() => {
                            modalType = ModalType.CancelEdit
                            showModal()
                        }}
                    >
                        <i class="fa-times fa-regular"/>
                        Stop editing
                    </button>
                {/if}
            </div>

            <div class="flex flex-col md:flex-row gap-6 ">
                <div class="grid basis-1/2 grid-cols-[min-content,auto] gap-x-3 items-center">
                    <XfaiBall class="w-14 h-14 row-span-2 text-xerteal-700" type="animated" />
                    <span class="col-start-2 font-bold text-lg"> Forecast AI </span>
                    <span class="slate-500"> Choose one of the following three options to generate and schedule a training plan that you can follow to achieve a set target. </span>
                    <div class="col-span-full flex flex-wrap gap-y-5 gap-x-2 mt-7 justify-evenly">
                        {#each programOptionBtns.filter((option)=>AIProgramTypes.includes(option.value)) as {name, id, value, icon}}
                        <div class="target-button">
                            <input class="hidden" type="radio" name="program-type" value={value} id={id} bind:group={selectedProgramType} 
                                on:change={changeProgramSetupForm} 
                            />
                            <label for={id} class="flex flex-col gap-1 justify-center items-center p-2 aspect-square rounded border border-[--color-xert-teal] text-[--color-xert-teal] text-center w-28 xl:w-32 2xl:w-36"> 
                                <i class={`fa-light ${icon} text-4xl xl:text-5xl`}/>
                                <div class="text-lg xl:text-xl">
                                    {name}
                                </div>
                            </label>
                        </div>
                        {/each}
                    </div>
                </div>

                <div class="h-px md:w-px md:h-auto bg-black/10"/>

                <div class="grid basis-1/2 grid-cols-[min-content,auto] gap-x-3 items-center">
                    <XataLogo class="w-14 h-14 row-span-2 text-xerteal-700" type="animated" />
                    <span class="col-start-2 font-bold text-lg"> Adaptive Training Advisor </span>
                    <span class="slate-500"> Choose one of the following three options to follow an adaptive program that will give you training day-by-day. </span>
                    <div class="col-span-full flex flex-wrap gap-y-5 gap-x-2 mt-7 justify-evenly">
                        {#each programOptionBtns.filter((option)=>!AIProgramTypes.includes(option.value)) as {name, id, value, icon}}
                        <div class="target-button">
                            <input class="hidden" type="radio" name="program-type" value={value} id={id} bind:group={selectedProgramType} 
                                on:change={changeProgramSetupForm} 
                            />
                            <label for={id} class="flex flex-col gap-1 justify-center items-center p-2 aspect-square rounded border border-[--color-xert-teal] text-[--color-xert-teal] text-center w-28 xl:w-32 2xl:w-36"> 
                                <i class={`fa-light ${icon} text-4xl xl:text-5xl`}/>
                                <div class="text-lg xl:text-xl">
                                    {name}
                                </div>
                            </label>
                        </div>
                        {/each}
                    </div>
                </div>

            </div>
        </div>

        <div id="program-pages" class="flex flex-col items-center w-full">
        {#if selectedProgramType === ProgramType.BaseBuildPeak}
            <XataProgramPage
                formComponent={BBPSetupForm}
                programType={ProgramType.BaseBuildPeak}
                plan={bbpPlan}
                on:save={() => openSaveModal(() => bbpPlanBody($bbpPlan))}
            />
        {/if}

        {#if selectedProgramType == ProgramType.Challenge}
            <XataProgramPage
                formComponent={ChallengeSetupForm}
                programType={ProgramType.Challenge}
                plan={challengePlan}
                on:save={() => openSaveModal(() => challengePlanBody($challengePlan))}
            />
        {/if}

        {#if selectedProgramType === ProgramType.Continuous}
            <XataProgramPage
                formComponent={ContinuousSetupForm}
                programType={ProgramType.Continuous}
                plan={continuousPlan}
                on:save={() => openSaveModal(() => continuousPlanBody($continuousPlan))}
            />
        {/if}

        {#if selectedProgramType == ProgramType.Goal}
            <XfaiProgramPage 
                formComponent={GoalSetupForm}
                programType={ProgramType.Goal}
                plan={goalPlan}
                bind:forecastResults={forecastResults[ProgramType.Goal]}
                bind:cancelForecast
                bind:adapt
                bind:savingResults
                bind:scheduleWorkouts
                onSave={() => {openSaveModal(() => goalPlanBody($goalPlan))}}
            />
        {/if}

        {#if selectedProgramType == ProgramType.Event}
            <XfaiProgramPage 
                formComponent={EventSetupForm}
                programType={ProgramType.Event}
                plan={eventPlan}
                bind:forecastResults={forecastResults[ProgramType.Event]}
                bind:cancelForecast
                bind:adapt
                bind:savingResults
                bind:scheduleWorkouts
                onSave={() => {openSaveModal(() => eventPlanBody($eventPlan))}}
            />
        {/if}
        
        {#if selectedProgramType === ProgramType.Race && isGoogleMapsLoaded}
            {#if currentProgramType !== ProgramType.Race || currentTP_raceRecords}
                <XfaiProgramPage 
                    formComponent={RaceSetupForm}
                    programType={ProgramType.Race}
                    plan={racePlan}
                    bind:forecastResults={forecastResults[ProgramType.Race]}
                    bind:cancelForecast
                    bind:adapt
                    bind:savingResults
                    bind:scheduleWorkouts
                onSave={() => {openSaveModal(() => racePlanBody($racePlan))}}
                />
            {:else}
                <div class="text-center">
                    <i class="fa-solid fa-spin fa-spinner text-5xl text-xerteal-700"/>
                </div>
            {/if}
        {/if}
        </div>   
    {/if}
</div>


{#if updateInProgress}
    <CyclingProgress overlay listen={['progress']} title="Building your training plan"
        messages={["Laying out your training days…", "Matching workouts to your goals…", "Pacing your training load…", "Filling your calendar…"]}/>
{/if}

<Modal bind:show={showModal} bind:close={closeModal} header={false}>

    {#if modalType === ModalType.Save}
        <div class="grid grid-cols-[min-content,auto] gap-x-7 items-center gap-y-1">
            <i class="fa-light fa-circle-info text-5xl text-green-600 row-span-2"/>
            <span class="col-start-2 text-lg font-bold">Change training program</span>
            <span class="text-slate-600 text-base">{confirmationWarning}</span>   
        </div>
    {:else if modalType === ModalType.SwitchTab || ModalType.CancelEdit}
        <div class="grid grid-cols-[min-content,auto] gap-x-7 items-center gap-y-1">
            <i class="fa-light fa-warning text-5xl text-yellow-600 row-span-2"/>
            <span class="col-start-2 text-lg font-bold">{modalType === ModalType.CancelEdit ? "Stop changing programs" : "Switch program setup type"}</span>
            <span class="text-slate-600 text-base">You will lose progress from your current program setup.</span>   
        </div>
    {/if}

    <svelte:fragment slot="footer">
        <div class="flex gap-2 justify-end items-baseline">
            
            <button class="btn-xfai border border-slate-600 text-slate-600 bg-white hover:bg-[--color-xert-blue] hover:text-white hover:border-[--color-xert-blue]" 
                on:click={() => {
                    closeModal()
                }}
            >
                Cancel
            </button>
            <button disabled={updateInProgress} class="btn-xfai border-none text-white bg-[--color-xert-teal] hover:bg-[--color-xert-blue]" 
                on:click={() => {
                    if (modalType === ModalType.Save) {
                        updateTrainingProgram()
                        closeModal()
                    } else if (modalType === ModalType.CancelEdit) {
                        isChangingPlan = false
                        selectedProgramType = currentProgramType
                        setPlanDefaults()
                        closeModal()
                    } else if (modalType === ModalType.SwitchTab) {
                        isChangingPlan = false
                        selectedProgramType = currentProgramType
                        setPlanDefaults()
                        closeModal()
                        switchMyFitnessTab(nextTab, root)
                    }
                }}
            >
                Continue
            </button>

        </div>
    </svelte:fragment>    
</Modal>

<style lang="scss">

    .target-button{
        input[type=radio]{
            + label {
                background: rgba(var(--rgb-xert-teal), 0.2);
                transition: all .2s ease-in-out;
                box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.2), 0 2px 4px -2px rgb(0 0 0 / 0.2);
            }
            &:checked + label {
                transform: scale(1.1);
                box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.2), 0 4px 6px -4px rgb(0 0 0 / 0.2);
                font-weight: bold;
                > i {
                    font-weight: bold;
                }
            }
            &:hover + label {
                transform: scale(1.1);
                box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.2), 0 4px 6px -4px rgb(0 0 0 / 0.2);
                font-weight: bold;
                cursor: pointer;
                > i {
                    font-weight: bold;
                }
            }
        } 
    }
</style>
    